import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { EmptyState, LoadingState } from "@/components/ui/StateBoundaries";
import { listMenu } from "@/lib/adminApi";
import { inr } from "@/lib/format";

/**
 * Recipes (owner, 2026-10-30; spec docs/superpowers/specs/2026-10-31-recipes-design.md).
 *
 * What one plate of a dish — or of one variant, or one add-on — takes from stock, and
 * a report of what the sales should have used beside what the stock ledger says was
 * used. Since 2026-10-31 (owner) the server takes a bill's recipe stock when the bill
 * is printed, off the bill path, and gives it back if the bill is reopened or cancelled.
 * Phase 3 (owner, 2026-11-01): sub-recipes, usable % on stock items, add-on recipes and
 * food cost per dish.
 */
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });
const reason = err => err?.response?.data?.detail || err?.message || "no answer from the server";
const today = () => new Date().toISOString().slice(0, 10);

// A line is a stock item ("s:12") or a sub-recipe ("r:3") in the picker.
const lineRef = l => (l.sub_recipe_id ? `r:${l.sub_recipe_id}` : l.stock_item_id ? `s:${l.stock_item_id}` : "");
const fromRef = ref => (ref.startsWith("r:") ? { stock_item_id: null, sub_recipe_id: Number(ref.slice(2)) }
  : ref.startsWith("s:") ? { stock_item_id: Number(ref.slice(2)), sub_recipe_id: null } : { stock_item_id: null, sub_recipe_id: null });
const toBody = lines => lines.map(l => ({ ...(l.sub_recipe_id ? { sub_recipe_id: Number(l.sub_recipe_id) }
  : { stock_item_id: Number(l.stock_item_id) }), quantity: Number(l.quantity) }));

function RecipeLines({ title, note, lines, stockItems, subRecipes, canWrite, onSave, testId, saveWord = "Save recipe" }) {
  const [draft, setDraft] = useState(lines);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(lines), [lines]);
  const units = useMemo(() => ({
    ...Object.fromEntries(stockItems.map(s => [`s:${s.id}`, s.unit])),
    ...Object.fromEntries(subRecipes.map(s => [`r:${s.id}`, s.unit])),
  }), [stockItems, subRecipes]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(lines);
  const valid = draft.every(l => lineRef(l) && Number(l.quantity) > 0);

  const save = async () => {
    setBusy(true);
    try {
      await onSave(toBody(draft));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border bg-card p-base space-y-snug" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-snug">
        <h3 className="text-body font-semibold">{title}</h3>
        {note && <span className="text-meta text-muted-foreground">{note}</span>}
      </div>
      {draft.length === 0 && <p className="text-meta text-muted-foreground">No stock items yet.</p>}
      {draft.map((line, i) => (
        <div key={i} className="flex items-center gap-snug">
          <select
            className="h-10 flex-1 min-w-0 rounded-md border bg-background px-snug text-body"
            value={lineRef(line)}
            disabled={!canWrite}
            onChange={e => setDraft(d => d.map((x, j) => (j === i ? { ...x, ...fromRef(e.target.value) } : x)))}
            aria-label="Stock item or sub-recipe"
            data-testid={`${testId}-item-${i}`}
          >
            <option value="">Choose stock item…</option>
            <optgroup label="Stock items">
              {stockItems.map(s => <option key={`s${s.id}`} value={`s:${s.id}`}>{s.name}</option>)}
            </optgroup>
            {subRecipes.length > 0 && (
              <optgroup label="Sub-recipes">
                {subRecipes.map(s => <option key={`r${s.id}`} value={`r:${s.id}`}>{s.name}</option>)}
              </optgroup>
            )}
          </select>
          <Input
            type="number" inputMode="decimal" min="0" step="0.001"
            className="h-10 w-28 text-right tabular-nums"
            value={line.quantity}
            disabled={!canWrite}
            onChange={e => setDraft(d => d.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))}
            aria-label="Quantity"
            data-testid={`${testId}-qty-${i}`}
          />
          <span className="w-10 text-meta text-muted-foreground">{units[lineRef(line)] || ""}</span>
          {canWrite && (
            <Button variant="ghost" size="icon" aria-label="Remove line"
                    onClick={() => setDraft(d => d.filter((_, j) => j !== i))}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      {canWrite && (
        <div className="flex gap-snug">
          <Button variant="outline" size="sm"
                  onClick={() => setDraft(d => [...d, { stock_item_id: null, sub_recipe_id: null, quantity: "" }])}
                  data-testid={`${testId}-add`}>
            <Plus className="w-4 h-4" /> Add line
          </Button>
          <Button variant="action" size="sm" className="ml-auto" disabled={!dirty || !valid || busy} onClick={save}
                  data-testid={`${testId}-save`}>
            {busy ? "Saving…" : saveWord}
          </Button>
        </div>
      )}
    </section>
  );
}

function RecipeEditor({ token, stockItems, subRecipes, canWrite }) {
  const [menu, setMenu] = useState(null);
  const [dishId, setDishId] = useState("");
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    listMenu(token).then(setMenu).catch(err => setError(`Could not load the menu: ${reason(err)}`));
  }, [token]);
  useEffect(() => {
    if (!dishId) { setRecipe(null); return; }
    setRecipe(null);
    axios.get(`${API}/inventory/recipes/${dishId}`, auth(token))
      .then(r => setRecipe(r.data))
      .catch(err => setError(`Could not load the recipe: ${reason(err)}`));
  }, [dishId, token]);

  const dishes = useMemo(
    () => (menu || []).flatMap(cat => (cat.items || []).map(item => ({ id: item.id, name: item.name, category: cat.name }))),
    [menu],
  );

  const save = async (variationId, lines, modifierId = null) => {
    try {
      const r = await axios.put(`${API}/inventory/recipes/${dishId}`,
        { variation_id: variationId, ...(modifierId ? { modifier_id: modifierId } : {}), lines }, auth(token));
      setRecipe(r.data);
      toast.success("Recipe saved — bills printed from now on use it");
    } catch (err) {
      toast.error(`Recipe not saved: ${reason(err)}`);
    }
  };

  if (error) return <p className="text-meta text-danger" role="alert">{error}</p>;
  if (!menu) return <LoadingState message="Loading the menu…" />;
  const common = { stockItems, subRecipes, canWrite };
  return (
    <div className="space-y-base" data-testid="recipe-editor">
      <select
        className="h-11 w-full max-w-md rounded-md border bg-background px-snug text-body"
        value={dishId}
        onChange={e => setDishId(e.target.value)}
        aria-label="Dish"
        data-testid="recipe-dish-select"
      >
        <option value="">Choose a dish…</option>
        {dishes.map(d => <option key={d.id} value={d.id}>{d.name} · {d.category}</option>)}
      </select>
      {dishId && !recipe && <LoadingState message="Loading the recipe…" />}
      {recipe && (
        <div className="grid gap-base lg:grid-cols-2">
          <RecipeLines
            title={recipe.variations.length ? "Base recipe" : "Recipe"}
            note={recipe.variations.length ? "used by every variant without its own" : "per plate"}
            lines={recipe.base} {...common}
            onSave={lines => save(null, lines)} testId="recipe-base"
          />
          {recipe.variations.map(v => (
            <RecipeLines
              key={v.variation_id}
              title={v.name}
              note={v.uses_base ? "uses the base recipe" : "own recipe"}
              lines={v.lines} {...common}
              onSave={lines => save(v.variation_id, lines)} testId={`recipe-variation-${v.variation_id}`}
            />
          ))}
          {(recipe.addons || []).map(a => (
            <RecipeLines
              key={`a${a.modifier_id}`}
              title={`Add-on · ${a.name}`}
              note="per plate it is ordered on"
              lines={a.lines} {...common}
              onSave={lines => save(null, lines, a.modifier_id)} testId={`recipe-addon-${a.modifier_id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SubRecipes({ token, stockItems, subRecipes, error, canWrite, onChanged }) {
  const blank = { id: null, name: "", unit: "", yield_quantity: "", lines: [] };
  const [editing, setEditing] = useState(null);
  const save = async lines => {
    const body = { name: editing.name.trim(), unit: editing.unit.trim(), yield_quantity: Number(editing.yield_quantity), lines };
    try {
      if (editing.id) await axios.put(`${API}/inventory/recipes/sub-recipes/${editing.id}`, body, auth(token));
      else await axios.post(`${API}/inventory/recipes/sub-recipes`, body, auth(token));
      toast.success(`${body.name} saved — every dish using it follows`);
      setEditing(null);
      onChanged();
    } catch (err) {
      toast.error(`Sub-recipe not saved: ${reason(err)}`);
    }
  };
  const remove = async sub => {
    try {
      await axios.delete(`${API}/inventory/recipes/sub-recipes/${sub.id}`, auth(token));
      toast.success(`${sub.name} deleted`);
      setEditing(null);
      onChanged();
    } catch (err) {
      toast.error(`Not deleted: ${reason(err)}`);
    }
  };

  if (error) return <p className="text-meta text-danger" role="alert" data-testid="sub-recipes-error">Sub-recipes not available: {error}</p>;
  const ready = editing && editing.name.trim() && editing.unit.trim() && Number(editing.yield_quantity) > 0;
  return (
    <section className="space-y-snug" data-testid="sub-recipes">
      <div className="flex items-start gap-base">
        <p className="text-meta text-muted-foreground flex-1">
          A sub-recipe is something made once and used in many dishes — a gravy, a dough, a marinade. Say what one batch
          makes and what it takes; a dish then uses an amount of it like any stock item.
        </p>
        {canWrite && !editing && (
          <Button variant="action" size="sm" onClick={() => setEditing(blank)} data-testid="sub-recipe-new">
            <Plus className="w-4 h-4" /> New sub-recipe
          </Button>
        )}
      </div>
      {editing && (
        <div className="rounded-lg border bg-card p-base space-y-snug" data-testid="sub-recipe-editor">
          <div className="grid gap-snug sm:grid-cols-3">
            <label className="flex flex-col gap-tight text-meta font-medium">Name
              <Input value={editing.name} onChange={e => setEditing(x => ({ ...x, name: e.target.value }))} data-testid="sub-recipe-name" />
            </label>
            <label className="flex flex-col gap-tight text-meta font-medium">One batch makes
              <Input type="number" min="0" step="0.001" value={editing.yield_quantity}
                     onChange={e => setEditing(x => ({ ...x, yield_quantity: e.target.value }))} data-testid="sub-recipe-yield" />
            </label>
            <label className="flex flex-col gap-tight text-meta font-medium">Unit
              <Input value={editing.unit} placeholder="l, kg, piece" onChange={e => setEditing(x => ({ ...x, unit: e.target.value }))}
                     data-testid="sub-recipe-unit" />
            </label>
          </div>
          {!ready && <p className="text-micro text-muted-foreground">Give it a name, what one batch makes and the unit, then its lines.</p>}
          <RecipeLines
            title="One batch takes" lines={editing.lines} stockItems={stockItems}
            subRecipes={subRecipes.filter(s => s.id !== editing.id)} canWrite={canWrite && Boolean(ready)}
            onSave={save} testId="sub-recipe-lines" saveWord="Save sub-recipe"
          />
          <div className="flex gap-snug">
            {editing.id && (
              <Button variant="destructive" size="sm" onClick={() => remove(editing)} data-testid="sub-recipe-delete">
                <Trash2 className="w-4 h-4" /> Delete
              </Button>
            )}
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => setEditing(null)}>Close</Button>
          </div>
        </div>
      )}
      {subRecipes.length === 0 && !editing && (
        <EmptyState title="No sub-recipes yet" description="Make one for a gravy or base used by several dishes." />
      )}
      {subRecipes.length > 0 && (
        <div className="divide-y rounded-lg border bg-card">
          {subRecipes.map(s => (
            <button key={s.id} type="button" onClick={() => setEditing({ ...s, yield_quantity: String(s.yield_quantity) })}
                    className="w-full text-left px-base py-snug hover:bg-muted flex items-center gap-base"
                    data-testid={`sub-recipe-row-${s.id}`}>
              <span className="flex-1 min-w-0">
                <span className="text-body font-semibold">{s.name}</span>
                <span className="text-meta text-muted-foreground"> · a batch makes {s.yield_quantity} {s.unit}</span>
                <span className="block text-meta text-muted-foreground truncate">
                  {s.lines.map(l => `${l.stock_item_name} ${l.quantity} ${l.unit}`).join(" · ")}
                </span>
              </span>
              <span className="text-meta text-muted-foreground">{s.dishes} {s.dishes === 1 ? "dish" : "dishes"}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function FoodCost({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/inventory/recipes/food-cost`, auth(token))
      .then(r => setData(r.data))
      .catch(err => setError(reason(err)));
  }, [token]);
  if (error) return <p className="text-meta text-danger" role="alert" data-testid="food-cost-error">Food cost not available: {error}</p>;
  if (!data) return <LoadingState message="Working out food cost…" />;
  if (!data.rows.length) return <EmptyState title="No dish has a recipe yet" description="Food cost appears once a dish has a recipe." />;
  const flagged = data.rows.filter(r => r.alert).length;
  return (
    <section className="space-y-snug" data-testid="food-cost">
      <p className="text-meta text-muted-foreground">
        Cost of one plate from its recipe at the average price you paid, and at your latest purchase price. Rows whose
        latest cost is {data.alert_pct}% or more above average are marked
        {flagged ? ` — ${flagged} now` : ""}. Change the % under Settings → Operations → Recipes.
      </p>
      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-body">
          <thead className="bg-muted/50 text-meta text-muted-foreground">
            <tr>
              <th className="px-base py-snug text-left font-medium">Dish</th>
              <th className="px-base py-snug text-right font-medium">Price</th>
              <th className="px-base py-snug text-right font-medium">Cost</th>
              <th className="px-base py-snug text-right font-medium">Cost %</th>
              <th className="px-base py-snug text-right font-medium">Margin</th>
              <th className="px-base py-snug text-right font-medium">At latest price</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={`${r.menu_item_id}-${r.variation_id || 0}-${r.modifier_id || 0}`} className="border-t"
                  data-testid={`food-cost-row-${r.menu_item_id}-${r.variation_id || 0}-${r.modifier_id || 0}`}>
                <td className="px-base py-snug">
                  <div className="font-medium">{r.label}</div>
                  {r.not_costed.length > 0 && (
                    <div className="text-meta text-warning">Not costed (never bought with a price): {r.not_costed.join(", ")}</div>
                  )}
                </td>
                <td className="px-base py-snug text-right tabular-nums">{inr(r.price)}</td>
                <td className="px-base py-snug text-right tabular-nums">{inr(r.cost)}</td>
                <td className="px-base py-snug text-right tabular-nums">{r.cost_pct == null ? "—" : `${r.cost_pct}%`}</td>
                <td className="px-base py-snug text-right tabular-nums">{inr(r.margin)}</td>
                <td className={`px-base py-snug text-right tabular-nums ${r.alert ? "text-danger font-semibold" : ""}`}>
                  {inr(r.cost_latest)}
                  {r.change_pct != null && r.change_pct !== 0 && (
                    <span className="block text-meta">{r.change_pct > 0 ? "+" : ""}{r.change_pct}%{r.alert ? " — check the price" : ""}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UsageReport({ token }) {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    axios.get(`${API}/inventory/recipes/usage`, { ...auth(token), params: { from_date: from, to_date: to } })
      .then(r => setData(r.data))
      .catch(err => setError(`Recipe usage not available: ${reason(err)}`));
  }, [from, to, token]);

  return (
    <section className="space-y-snug" data-testid="recipe-usage">
      <div className="flex flex-wrap items-center gap-snug">
        <h3 className="text-body font-semibold mr-auto">What sales should have used vs the stock ledger</h3>
        <DatePicker value={from} max={to} onChange={v => setFrom(v || today())} ariaLabel="From" testId="recipe-usage-from" />
        <DatePicker value={to} min={from} onChange={v => setTo(v || today())} ariaLabel="To" testId="recipe-usage-to" />
      </div>
      {error && <p className="text-meta text-danger" role="alert">{error}</p>}
      {!error && !data && <LoadingState message="Working out usage…" />}
      {data && data.rows.length === 0 && (
        <EmptyState title="Nothing to compare" description="No sold dish with a recipe and no consumption recorded in these days." />
      )}
      {data && data.rows.length > 0 && (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-body">
            <thead className="bg-muted/50 text-meta text-muted-foreground">
              <tr>
                <th className="px-base py-snug text-left font-medium">Stock item</th>
                <th className="px-base py-snug text-right font-medium">Should have used</th>
                <th className="px-base py-snug text-right font-medium">Ledger used</th>
                <th className="px-base py-snug text-right font-medium">Difference</th>
                <th className="px-base py-snug text-right font-medium">₹</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.stock_item_id} className="border-t" data-testid={`recipe-usage-row-${r.stock_item_id}`}>
                  <td className="px-base py-snug">
                    <div className="font-medium">{r.name}</div>
                    {r.dishes.length > 0 && (
                      <div className="text-meta text-muted-foreground">{r.dishes.map(d => `${d.dish} ×${d.sold}`).join(" · ")}</div>
                    )}
                  </td>
                  <td className="px-base py-snug text-right tabular-nums">{r.theoretical} {r.unit}</td>
                  <td className="px-base py-snug text-right tabular-nums">{r.actual} {r.unit}</td>
                  <td className={`px-base py-snug text-right tabular-nums font-medium ${r.variance > 0 ? "text-danger" : ""}`}>
                    {r.variance > 0 ? "+" : ""}{r.variance} {r.unit}
                  </td>
                  <td className="px-base py-snug text-right tabular-nums">{r.variance_value == null ? "—" : inr(r.variance_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.dishes_without_recipe.length > 0 && (
        <p className="text-meta text-muted-foreground" data-testid="recipe-usage-missing">
          Sold without a recipe yet (not counted above): {data.dishes_without_recipe.map(d => `${d.dish} ×${d.sold}`).join(" · ")}
        </p>
      )}
    </section>
  );
}

/** The bill-print deduction's own state, in words (operating law 3). */
function DeductionStatus({ token }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/inventory/recipes/deduction-status`, auth(token))
      .then(r => setState(r.data))
      .catch(err => setError(reason(err)));
  }, [token]);
  if (error) return <p className="text-meta text-danger" role="alert">Stock deduction status not available: {error}</p>;
  if (!state) return null;
  return (
    <div className="space-y-tight" data-testid="recipe-deduction-status">
      {state.last_error && (
        <p className="text-meta text-danger" role="alert">Stock deduction stopped: {state.last_error}</p>
      )}
      {state.failed?.map(f => (
        <p key={f.order_id} className="text-meta text-danger" role="alert">
          Order {f.order_id}: stock not deducted — {f.reason}
        </p>
      ))}
      {!state.last_run && <p className="text-meta text-muted-foreground">Stock deduction has not run yet since the server started.</p>}
    </div>
  );
}

const TABS = [["dishes", "Dish recipes"], ["subs", "Sub-recipes"], ["cost", "Food cost"], ["usage", "Usage"]];

export default function RecipesPanel({ token, stockItems, canWrite }) {
  const [tab, setTab] = useState("dishes");
  const [subRecipes, setSubRecipes] = useState([]);
  const [subError, setSubError] = useState(null);
  const loadSubs = useCallback(() => {
    axios.get(`${API}/inventory/recipes/sub-recipes`, auth(token))
      .then(r => { setSubRecipes(r.data); setSubError(null); })
      .catch(err => { setSubRecipes([]); setSubError(reason(err)); });
  }, [token]);
  useEffect(() => { loadSubs(); }, [loadSubs]);

  return (
    <div className="space-y-section" data-testid="recipes-panel">
      <p className="text-meta text-muted-foreground">
        A recipe is what one plate takes from stock. When a bill is printed, its dishes' recipe stock comes off
        automatically (a dish without a recipe takes nothing); a reopened or cancelled bill gives it back. Stock
        can go below zero — it never stops a bill.
      </p>
      <DeductionStatus token={token} />
      <div className="flex flex-wrap gap-tight" role="tablist">
        {TABS.map(([key, label]) => (
          <Button key={key} size="sm" variant={tab === key ? "default" : "outline"} role="tab" aria-selected={tab === key}
                  onClick={() => setTab(key)} data-testid={`recipes-tab-${key}`}>{label}</Button>
        ))}
      </div>
      {tab === "dishes" && <RecipeEditor token={token} stockItems={stockItems} subRecipes={subRecipes} canWrite={canWrite} />}
      {tab === "subs" && (
        <SubRecipes token={token} stockItems={stockItems} subRecipes={subRecipes} error={subError} canWrite={canWrite}
                    onChanged={loadSubs} />
      )}
      {tab === "cost" && <FoodCost token={token} />}
      {tab === "usage" && <UsageReport token={token} />}
    </div>
  );
}
